import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { printReportService } from '../../services/printReportService';
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  CreditCard,
  AccountBalance,
  LocalAtm,
  Payment,
  Receipt,
  TrendingUp,
  Refresh,
  FileDownload,
  Print,
  CheckCircle,
  Cancel,
  Pending,
  Euro,
  Close,
  Search,
  Today,
  DateRange,
  CalendarMonth,
  AttachMoney,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
// Theme not needed - white background

// Extract order-to-payment conversion to avoid duplication
const convertOrderToPayment = (doc: any, t: any) => {
  const data = doc.data();
  const status = (data.status || '').toLowerCase();
  const paymentStatus = (data.payment_status || data.paymentStatus || '').toLowerCase();
  const isRelevant = status === 'delivered' || status === 'completed' || paymentStatus === 'completed';
  const isCancelled = status === 'cancelled';

  if (!isRelevant || isCancelled) return null;

  const orderNumber = data.order_number || doc.id;
  const createdAt = toISOString(data.created_at);

  return {
    id: doc.id,
    order_id: doc.id,
    order_number: orderNumber,
    customer_name: data.customer_name || t('payments.walk_in_customer'),
    amount: parseFloat(String(data.total || 0)),
    payment_method: data.payment_method || 'cash',
    status: paymentStatus === 'refunded' ? 'refunded' : 'completed',
    transaction_id: data.transaction_id || `TXN-${orderNumber.toString().replace(/[^A-Z0-9]/gi, '').slice(-8).toUpperCase()}`,
    created_at: createdAt,
    created_by: data.created_by || data.createdBy || data.processed_by || 'POS',
    table_number: data.table_number || null,
    order_type: data.order_type || data.type || 'dine-in',
    items_count: Array.isArray(data.items) ? data.items.length : 0,
    is_split_payment: data.is_split_payment || false,
    split_person_name: data.split_person_name || null,
    split_total_people: data.split_total_people || null,
    split_person_index: data.split_person_index || null,
    discount_amount: data.discount_amount || 0,
    discount_type: data.discount_type || '',
    discount_value: data.discount_value || 0,
    discount_reason: data.discount_reason || '',
    total_before_discount: data.total_before_discount || 0,
  };
};

const toISOString = (val: any): string => {
  if (!val) return new Date().toISOString();
  if (val.toDate && typeof val.toDate === 'function') return val.toDate().toISOString();
  if (val instanceof Date) return val.toISOString();
  if (typeof val === 'string') return val;
  return new Date().toISOString();
};

const ModernPaymentsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<any | null>(null);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Search & filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [quickDateFilter, setQuickDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sort
  const [sortField, setSortField] = useState<'date' | 'amount'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportLanguage, setExportLanguage] = useState<'en' | 'ar'>(language as 'en' | 'ar');

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const paymentsRef = useRef<any[]>([]);

  // Payment method icon
  const getPaymentIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card': return <CreditCard sx={{ fontSize: 18 }} />;
      case 'cash': return <LocalAtm sx={{ fontSize: 18 }} />;
      case 'paypal': return <AccountBalance sx={{ fontSize: 18 }} />;
      default: return <Payment sx={{ fontSize: 18 }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': case 'success': return 'success';
      case 'pending': return 'warning';
      case 'refunded': return 'info';
      case 'failed': case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': case 'success': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'pending': return <Pending sx={{ fontSize: 16 }} />;
      case 'failed': case 'cancelled': return <Cancel sx={{ fontSize: 16 }} />;
      default: return <Pending sx={{ fontSize: 16 }} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': case 'success': return t('payments.status_completed');
      case 'pending': return t('payments.status_pending');
      case 'failed': return t('payments.status_failed');
      case 'cancelled': return t('payments.status_cancelled');
      case 'refunded': return t('payments.status_refunded');
      default: return status;
    }
  };

  // Fetch payments from Firestore orders
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { collection, getDocs, query, orderBy: fsOrderBy, limit: fsLimit } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      const ordersRef = collection(db, 'orders');
      let allDocs: any[] = [];

      try {
        // Limit to 500 most recent orders for performance
        const q = query(ordersRef, fsOrderBy('created_at', 'desc'), fsLimit(500));
        const snapshot = await getDocs(q);
        allDocs = snapshot.docs;
      } catch {
        const q = query(ordersRef, fsLimit(500));
        const snapshot = await getDocs(q);
        allDocs = snapshot.docs;
      }

      const paymentsData: any[] = [];
      for (const doc of allDocs) {
        const payment = convertOrderToPayment(doc, t);
        if (payment) paymentsData.push(payment);
      }

      paymentsData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      paymentsRef.current = paymentsData;
      setPayments(paymentsData);
    } catch (err) {
      console.error('[PAYMENTS] Failed to fetch:', err);
      setError('Failed to load payments. Please try again.');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let mounted = true;

    const setupListener = async () => {
      try {
        const { collection, query, orderBy: fsOrderBy, onSnapshot, limit: fsLimit } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        const ordersRef = collection(db, 'orders');
        const q = query(ordersRef, fsOrderBy('created_at', 'desc'), fsLimit(500));

        unsubscribe = onSnapshot(q, (snapshot) => {
          if (!mounted) return;
          const paymentsData: any[] = [];
          for (const doc of snapshot.docs) {
            const payment = convertOrderToPayment(doc, t);
            if (payment) paymentsData.push(payment);
          }
          paymentsData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          paymentsRef.current = paymentsData;
          setPayments(paymentsData);
          setLoading(false);
          setError(null);
        }, (err) => {
          console.error('[PAYMENTS] Listener error:', err);
          if (mounted) fetchPayments();
        });
      } catch (err) {
        console.error('[PAYMENTS] Listener setup failed:', err);
        if (mounted) fetchPayments();
      }
    };

    setLoading(true);
    setupListener();
    return () => { mounted = false; if (unsubscribe) unsubscribe(); };
  }, [fetchPayments, t]);

  // Filtered & searched payments (all client-side for speed)
  const filteredPayments = useMemo(() => {
    let result = [...payments];

    // Quick date filter
    if (quickDateFilter !== 'all') {
      const now = new Date();
      let start: Date;
      if (quickDateFilter === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (quickDateFilter === 'week') {
        start = new Date(now);
        start.setDate(start.getDate() - 7);
      } else {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      result = result.filter(p => new Date(p.created_at) >= start);
    }

    // Method filter
    if (methodFilter !== 'all') {
      result = result.filter(p => p.payment_method.toLowerCase() === methodFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status.toLowerCase() === statusFilter);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        (p.order_number || '').toString().toLowerCase().includes(q) ||
        (p.customer_name || '').toLowerCase().includes(q) ||
        (p.transaction_id || '').toLowerCase().includes(q) ||
        (p.id || '').toLowerCase().includes(q) ||
        `${p.amount}`.includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortField === 'date') {
        const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        return sortDir === 'desc' ? -diff : diff;
      } else {
        const diff = a.amount - b.amount;
        return sortDir === 'desc' ? -diff : diff;
      }
    });

    return result;
  }, [payments, quickDateFilter, methodFilter, statusFilter, searchQuery, sortField, sortDir]);

  // KPI calculations
  const kpis = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);

    const todayPayments = payments.filter(p => new Date(p.created_at) >= todayStart);
    const yesterdayPayments = payments.filter(p => {
      const d = new Date(p.created_at);
      return d >= yesterdayStart && d < todayStart;
    });

    const totalRevenue = filteredPayments.reduce((sum, p) => sum + (isNaN(p.amount) ? 0 : p.amount), 0);
    const todayRevenue = todayPayments.reduce((sum, p) => sum + (isNaN(p.amount) ? 0 : p.amount), 0);
    const yesterdayRevenue = yesterdayPayments.reduce((sum, p) => sum + (isNaN(p.amount) ? 0 : p.amount), 0);
    const avgTransaction = filteredPayments.length > 0 ? totalRevenue / filteredPayments.length : 0;

    // Payment method breakdown
    const methodBreakdown: Record<string, { count: number; amount: number }> = {};
    filteredPayments.forEach(p => {
      const method = p.payment_method.toLowerCase();
      if (!methodBreakdown[method]) methodBreakdown[method] = { count: 0, amount: 0 };
      methodBreakdown[method].count++;
      methodBreakdown[method].amount += isNaN(p.amount) ? 0 : p.amount;
    });

    const cashAmount = methodBreakdown['cash']?.amount || 0;
    const cardAmount = methodBreakdown['card']?.amount || 0;

    return {
      totalRevenue,
      totalTransactions: filteredPayments.length,
      todayRevenue,
      todayTransactions: todayPayments.length,
      yesterdayRevenue,
      avgTransaction,
      cashAmount,
      cardAmount,
      methodBreakdown,
    };
  }, [payments, filteredPayments]);

  // Paginated data
  const paginatedPayments = useMemo(() => {
    return filteredPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredPayments, page, rowsPerPage]);

  const handleRefund = (payment: any) => {
    setSelectedPayment(payment);
    setRefundAmount(payment.amount.toString());
    setOpenRefundDialog(true);
  };

  const handleProcessRefund = async () => {
    if (!selectedPayment) return;
    try {
      const { doc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const orderId = selectedPayment.order_id || selectedPayment.id;
      await updateDoc(doc(db, 'orders', orderId), {
        payment_status: 'refunded',
        refund_amount: parseFloat(refundAmount),
        refund_reason: refundReason,
        refunded_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      setPayments(prev => prev.map(p =>
        p.id === selectedPayment.id ? { ...p, status: 'refunded' } : p
      ));
      setOpenRefundDialog(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (err) {
      console.error('[PAYMENTS] Refund failed:', err);
    }
  };

  const handleSort = (field: 'date' | 'amount') => {
    if (sortField === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleViewDetail = (payment: any) => {
    setSelectedPayment(payment);
    setDetailDialogOpen(true);
  };

  // Export functions
  const loadLogoAsBase64 = async (): Promise<string> => {
    try {
      const resp = await fetch('/saheeh_logo_w.svg', { cache: 'no-cache' });
      if (resp.ok) {
        const blob = await resp.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string || '');
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.readAsDataURL(blob);
        });
      }
    } catch { /* fallback */ }
    try {
      const LogoCacheService = (await import('../../services/logoCacheService')).default;
      return await LogoCacheService.getLogo() || '';
    } catch { return ''; }
  };

  const generateExport = async () => {
    if (!filteredPayments.length) return;

    if (exportFormat === 'csv') {
      const totalRev = filteredPayments.reduce((s, p) => s + (p.amount || 0), 0);
      let csv = `SAHEEH SUSHI & BOWLS - Payment Report\nGenerated,${new Date().toLocaleString()}\n\n`;
      csv += `Total Revenue,${totalRev.toFixed(2)}\nTotal Transactions,${filteredPayments.length}\n\n`;
      csv += `Order Number,Customer,Amount,Method,Status,Date\n`;
      filteredPayments.forEach(p => {
        csv += `${p.order_number},${p.customer_name},${p.amount.toFixed(2)},${p.payment_method},${p.status},${new Date(p.created_at).toLocaleDateString()}\n`;
      });
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `saheeh-payments-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // HTML/PDF
      const logoBase64 = await loadLogoAsBase64();
      const totalRev = filteredPayments.reduce((s, p) => s + (p.amount || 0), 0);
      const avg = filteredPayments.length > 0 ? totalRev / filteredPayments.length : 0;
      const reportHTML = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Payment Report</title>
        <style>body{font-family:Arial,sans-serif;margin:20px;color:#000}
        .header{text-align:center;border-bottom:3px solid #000;padding-bottom:20px;margin-bottom:30px}
        .logo{height:80px;margin:0 auto 10px;display:block}
        .summary{display:grid;grid-template-columns:repeat(3,1fr);gap:15px;margin-bottom:30px}
        .summary-card{background:#f8f9fa;padding:15px;border-radius:5px;text-align:center}
        .summary-value{font-size:22px;font-weight:bold;color:#1976D2}
        .summary-label{font-size:12px;color:#666;margin-top:5px}
        table{width:100%;border-collapse:collapse;margin-bottom:20px}
        th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:13px}
        th{background:#f2f2f2;font-weight:bold}
        .footer{text-align:center;font-size:11px;color:#666;border-top:1px solid #ccc;padding-top:15px;margin-top:30px}
        @media print{body{margin:0;padding:10px}}</style></head><body>
        <div class="header">
        ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="logo">` : '<div style="font-size:24px;font-weight:bold;color:#1976D2;margin-bottom:10px">SAHEEH</div>'}
        <h2 style="margin:5px 0">Payment Report</h2>
        <div style="font-size:13px;color:#666">Generated: ${new Date().toLocaleString()}</div></div>
        <div class="summary">
        <div class="summary-card"><div class="summary-value">${totalRev.toFixed(2)}</div><div class="summary-label">Total Revenue</div></div>
        <div class="summary-card"><div class="summary-value">${filteredPayments.length}</div><div class="summary-label">Transactions</div></div>
        <div class="summary-card"><div class="summary-value">${avg.toFixed(2)}</div><div class="summary-label">Average</div></div></div>
        <table><thead><tr><th>#</th><th>Order</th><th>Customer</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th></tr></thead><tbody>
        ${filteredPayments.map((p, i) => `<tr><td>${i + 1}</td><td>${p.order_number}</td><td>${p.customer_name}</td><td>${p.amount.toFixed(2)}</td><td>${p.payment_method}</td><td>${p.status}</td><td>${new Date(p.created_at).toLocaleDateString()}</td></tr>`).join('')}
        </tbody></table>
        <div class="footer"><div>SAHEEH SUSHI & BOWLS | www.saheehpos.com | All prices include 19% VAT</div></div></body></html>`;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(reportHTML);
        printWindow.document.close();
        printWindow.focus();
        if (exportFormat === 'pdf') {
          setTimeout(() => printWindow.print(), 800);
        }
      }
    }
    setExportDialogOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ color: '#9ca3af' }}>Loading payments...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchPayments}>Retry</Button>
        }>{error}</Alert>
      </Box>
    );
  }

  const SortIcon = ({ field }: { field: 'date' | 'amount' }) => {
    if (sortField !== field) return null;
    return sortDir === 'desc'
      ? <ArrowDownward sx={{ fontSize: 14, ml: 0.5 }} />
      : <ArrowUpward sx={{ fontSize: 14, ml: 0.5 }} />;
  };

  return (
    <Box sx={{
      height: { xs: 'auto', sm: '100vh' },
      minHeight: { xs: 'calc(100vh - 56px)' },
      overflow: { xs: 'auto', sm: 'hidden' },
      backgroundColor: '#252838',
      display: 'flex',
      flexDirection: 'column',
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* Header */}
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 2.5, pb: 1.5, borderBottom: '1px solid #3a3d52' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: { xs: 1, md: 0 } }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '1.4rem' }}>
              {t('payments.title') || 'Payments'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.3 }}>
              {filteredPayments.length} transactions {quickDateFilter !== 'all' ? `(${quickDateFilter})` : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownload sx={{ fontSize: 18 }} />}
              onClick={() => setExportDialogOpen(true)}
              sx={{
                borderColor: '#3a3d52',
                color: '#9ca3af',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: '#1976D2', color: '#1976D2', backgroundColor: '#2d3048' },
              }}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              onClick={() => { setLoading(true); fetchPayments(); }}
              sx={{
                borderColor: '#3a3d52',
                color: '#9ca3af',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: '#1976D2', color: '#1976D2', backgroundColor: '#2d3048' },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* KPI Cards Row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 1.5 }}>
          {[
            {
              icon: <TrendingUp sx={{ fontSize: 16, color: '#1976D2' }} />,
              label: 'Total Revenue',
              value: `€${kpis.totalRevenue.toFixed(2)}`,
              labelColor: '#90caf9',
              bg: 'rgba(33, 150, 243, 0.06)',
              border: 'rgba(33, 150, 243, 0.2)',
            },
            {
              icon: <Today sx={{ fontSize: 16, color: '#66bb6a' }} />,
              label: `Today (${kpis.todayTransactions})`,
              value: `€${kpis.todayRevenue.toFixed(2)}`,
              labelColor: '#a5d6a7',
              bg: 'rgba(76, 175, 80, 0.06)',
              border: 'rgba(76, 175, 80, 0.2)',
            },
            {
              icon: <Euro sx={{ fontSize: 16, color: '#e65100' }} />,
              label: 'Avg Transaction',
              value: `€${kpis.avgTransaction.toFixed(2)}`,
              labelColor: '#ffb74d',
              bg: 'rgba(237, 137, 54, 0.06)',
              border: 'rgba(237, 137, 54, 0.2)',
            },
            {
              icon: <Receipt sx={{ fontSize: 16, color: '#b39ddb' }} />,
              label: 'Cash / Card',
              value: `€${kpis.cashAmount.toFixed(0)} / €${kpis.cardAmount.toFixed(0)}`,
              labelColor: '#ce93d8',
              bg: 'rgba(156, 39, 176, 0.06)',
              border: 'rgba(156, 39, 176, 0.2)',
            },
          ].map((card) => (
            <Box key={card.label} sx={{
              py: 0.8,
              px: 1.5,
              borderRadius: '8px',
              backgroundColor: `${card.bg} !important`,
              border: `1px solid ${card.border}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                {card.icon}
                <Typography variant="caption" sx={{ color: card.labelColor, fontWeight: 500, fontSize: '0.68rem' }}>
                  {card.label}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#e0e0e0', lineHeight: 1.2 }}>
                {card.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Filters Row */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search order, customer..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: '#adb5bd' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: '100%', sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#2d3048',
                fontSize: '0.85rem',
                '& fieldset': { borderColor: '#3a3d52' },
              },
            }}
          />

          {/* Quick date chips */}
          {([
            { key: 'today', label: 'Today', icon: <Today sx={{ fontSize: 14 }} /> },
            { key: 'week', label: 'This Week', icon: <DateRange sx={{ fontSize: 14 }} /> },
            { key: 'month', label: 'This Month', icon: <CalendarMonth sx={{ fontSize: 14 }} /> },
            { key: 'all', label: 'All Time', icon: <AttachMoney sx={{ fontSize: 14 }} /> },
          ] as const).map(f => (
            <Chip
              key={f.key}
              icon={f.icon}
              label={f.label}
              size="small"
              onClick={() => { setQuickDateFilter(f.key); setPage(0); }}
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                backgroundColor: quickDateFilter === f.key ? '#1976D2' : '#2d3048',
                color: quickDateFilter === f.key ? '#fff' : '#495057',
                border: quickDateFilter === f.key ? 'none' : '1px solid #3a3d52',
                '& .MuiChip-icon': { color: quickDateFilter === f.key ? '#fff' : '#6c757d' },
                '&:hover': {
                  backgroundColor: quickDateFilter === f.key ? '#1565c0' : '#3a3d52',
                },
              }}
            />
          ))}

          {/* Method filter */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={methodFilter}
              onChange={(e) => { setMethodFilter(e.target.value); setPage(0); }}
              displayEmpty
              sx={{
                borderRadius: '8px',
                backgroundColor: '#2d3048',
                fontSize: '0.8rem',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a3d52' },
              }}
            >
              <MenuItem value="all">All Methods</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="paypal">PayPal</MenuItem>
            </Select>
          </FormControl>

          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              displayEmpty
              sx={{
                borderRadius: '8px',
                backgroundColor: '#2d3048',
                fontSize: '0.8rem',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3a3d52' },
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, md: 3 }, pt: 1, pb: { xs: 4, md: 2 } }}>
        <TableContainer sx={{ borderRadius: '8px', border: '1px solid #3a3d52', overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2 }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2 }}>Customer</TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('amount')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Amount <SortIcon field="amount" />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2 }}>Status</TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('date')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date <SortIcon field="date" />
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.8rem', backgroundColor: '#2d3048', py: 1.2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" sx={{ color: '#adb5bd' }}>
                      No payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPayments.map((payment, idx) => (
                  <TableRow
                    key={payment.id}
                    hover
                    onClick={() => handleViewDetail(payment)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#353850 !important' },
                      '& td': { py: 1, fontSize: '0.82rem', borderBottom: '1px solid #f0f0f0' },
                    }}
                  >
                    <TableCell sx={{ color: '#adb5bd', fontWeight: 500 }}>
                      {page * rowsPerPage + idx + 1}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0', fontSize: '0.82rem' }}>
                          {payment.order_number}
                        </Typography>
                        {payment.is_split_payment && (
                          <Chip label="Split" size="small" sx={{ fontSize: '0.6rem', height: 18, backgroundColor: 'rgba(33, 150, 243, 0.12)', color: '#1976D2' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#9ca3af' }}>
                      {payment.customer_name}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1976D2' }}>
                      {payment.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getPaymentIcon(payment.payment_method)}
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                          {payment.payment_method}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(payment.status)}
                        label={getStatusLabel(payment.status)}
                        color={getStatusColor(payment.status) as any}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                      {new Date(payment.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Print Receipt">
                          <IconButton
                            size="small"
                            onClick={() => printReportService.printPaymentReceipt(payment)}
                            sx={{ color: '#1976D2', '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.12)' } }}
                          >
                            <Print sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {payment.status === 'completed' && (
                          <Tooltip title="Refund">
                            <IconButton
                              size="small"
                              onClick={() => handleRefund(payment)}
                              sx={{ color: '#FF9800', '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.12)' } }}
                            >
                              <Refresh sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPayments.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #3a3d52',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.8rem' },
          }}
        />
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{
          backgroundColor: '#1976D2',
          color: '#fff',
          py: 1.5,
          px: 3,
          fontWeight: 600,
          fontSize: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          Payment Details
          <IconButton size="small" onClick={() => setDetailDialogOpen(false)} sx={{ color: '#fff' }}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedPayment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {[
                { label: 'Order Number', value: selectedPayment.order_number },
                { label: 'Customer', value: selectedPayment.customer_name },
                { label: 'Amount', value: `${selectedPayment.amount?.toFixed(2)}` },
                { label: 'Payment Method', value: selectedPayment.payment_method },
                { label: 'Status', value: selectedPayment.status, isChip: true },
                { label: 'Transaction ID', value: selectedPayment.transaction_id },
                { label: 'Date', value: new Date(selectedPayment.created_at).toLocaleString() },
                { label: 'Created By', value: selectedPayment.created_by },
                ...(selectedPayment.table_number ? [{ label: 'Table', value: `#${selectedPayment.table_number}` }] : []),
                ...(selectedPayment.is_split_payment ? [{ label: 'Split Payment', value: `${selectedPayment.split_person_name} (${selectedPayment.split_person_index}/${selectedPayment.split_total_people})` }] : []),
              ].map(({ label, value, isChip }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" sx={{ color: '#9ca3af', fontWeight: 500 }}>{label}</Typography>
                  {isChip ? (
                    <Chip
                      icon={getStatusIcon(value as string)}
                      label={getStatusLabel(value as string)}
                      color={getStatusColor(value as string) as any}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0' }}>{value}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #3a3d52' }}>
          <Button size="small" onClick={() => setDetailDialogOpen(false)} sx={{ color: '#9ca3af' }}>
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Print sx={{ fontSize: 16 }} />}
            onClick={() => {
              if (selectedPayment) printReportService.printPaymentReceipt(selectedPayment);
            }}
            sx={{ backgroundColor: '#1976D2', textTransform: 'none' }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={openRefundDialog} onClose={() => setOpenRefundDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ backgroundColor: '#FF9800', color: '#fff', py: 1.5, fontWeight: 600, fontSize: '1rem' }}>
          Process Refund
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>
            Refund for order: <strong>{selectedPayment?.order_number}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Refund Amount"
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><Euro sx={{ fontSize: 16 }} /></InputAdornment> }}
          />
          <TextField
            fullWidth
            label="Reason"
            multiline
            rows={2}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            size="small"
            placeholder="Enter reason for refund..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button size="small" onClick={() => setOpenRefundDialog(false)} sx={{ color: '#9ca3af' }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleProcessRefund} sx={{ backgroundColor: '#FF9800', textTransform: 'none' }}>
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Export Report
          <IconButton size="small" onClick={() => setExportDialogOpen(false)}><Close sx={{ fontSize: 18 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Format</InputLabel>
            <Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} label="Format">
              <MenuItem value="pdf">PDF (Print)</MenuItem>
              <MenuItem value="html">HTML (View)</MenuItem>
              <MenuItem value="csv">CSV (Spreadsheet)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Language</InputLabel>
            <Select value={exportLanguage} onChange={(e) => setExportLanguage(e.target.value as any)} label="Language">
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button size="small" onClick={() => setExportDialogOpen(false)} sx={{ color: '#9ca3af' }}>Cancel</Button>
          <Button size="small" variant="contained" startIcon={<FileDownload sx={{ fontSize: 16 }} />} onClick={generateExport} sx={{ backgroundColor: '#1976D2', textTransform: 'none' }}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModernPaymentsPage;
